// SAM-199 — front-desk SERVICE booking SUBMIT (the second half of the flow started in
// serviceBooking.ts / ServiceBookingSelector.tsx). Pure helpers only — no React, no API calls —
// so payload construction, idempotency-key handling and error mapping can be unit tested without
// mocking axios/Cognito. The actual POST lives in AddInvitation.tsx, which imports these.
//
// Endpoint contract (sam-lambda-functions, origin/dev):
//   - functions/booking/booking_orchestration.py::orchestrate_booking_payment — the request/
//     response shape this module builds/reads.
//   - functions/booking/lambda_function.py::payment_setup/payment_enroll/payment_validate — the
//     3DS chain that produces `auth_data` (see BookingPaymentService in portalApiService.ts).
//
// Access/QR: the backend grants business_access (the gate QR) itself, inside create_booking's
// modern write path (functions/booking/booking_access.py::grant_access_for_confirmed_booking,
// called from lambda_function.py::create_booking with `guest_info=body.get('on_behalf_of_guest')`
// once the booking reaches CONFIRMED). AddInvitation.tsx must NOT also call
// useCreateBusinessAccess for a serviceBooking submission — that would create a second, unlinked
// access for the same guest. Verified by reading booking_access.py's module docstring ("Why this
// exists") and its call site in lambda_function.py::create_booking (~line 2762).

import type { ServiceBookingSelection } from './serviceBooking';
import { formatPricingError } from '../../../utils/pricingErrors';

// ─── Idempotency key ─────────────────────────────────────────────────────────

/**
 * The SINGLE most important detail of this submit flow: a retry of the SAME attempt (e.g. a
 * dropped connection, a 5xx, the operator just clicking Confirm again after a decline) must reuse
 * the exact same key so the backend's own replay guard
 * (`sam_core.booking_orchestration`, unique on (tenant_id, idempotency_key)) resolves it to the
 * SAME row instead of taking a second engine hold / charging the card twice. `current` is
 * whatever the caller already generated for this attempt (see InvitationContext's
 * `serviceBookingIdempotencyKey`, cleared only when the underlying selection changes) — passing
 * it back here is a no-op; only a falsy `current` (first attempt, or the selection just changed)
 * mints a new one.
 */
export function getOrCreateIdempotencyKey(current: string | null | undefined): string {
  if (current) return current;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // crypto.randomUUID isn't available (old browser / non-secure context) — a timestamp + random
  // suffix is not cryptographically unique but is more than enough entropy to avoid an accidental
  // collision with another attempt within this endpoint's replay window.
  return `sb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ─── Reservation window ──────────────────────────────────────────────────────

export interface ReservationWindow {
  reservation_start: string;
  reservation_end: string;
}

/**
 * `selection.date` ("YYYY-MM-DD") + `selection.time` ("HH:MM" or "11:00 AM") combine into a NAIVE local
 * datetime string (no "Z", no offset) — matching how `booking.reservation_start` is actually
 * stored and read (see booking_access.py's "Validity window" docstring section: a naive
 * reservation_start IS ALREADY local wall-clock time, e.g. America/Santo_Domingo). Sending a "Z"
 * suffix here would make the backend treat this local slot time as UTC and silently shift it by
 * the tenant's offset.
 *
 * `reservation_end` adds `selection.durationMinutes` when the service has one; otherwise it falls
 * back to being equal to `reservation_start`, exactly like the backend's own default
 * (`booking_data.get("reservation_end", reservation_start)` in orchestrate_booking_payment).
 */
/**
 * The schedule hands the slot back as the label the picker shows, which is "HH:MM" for some services and
 * "11:00 AM" for others. The wire format is always 24-hour wall clock, so read either one.
 */
export function parseSlotTime(raw: string): { hours: number; minutes: number } | null {
  const m = /^\s*(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([AaPp])?\.?[Mm]?\.?\s*$/.exec(String(raw ?? ''));
  if (!m) return null;
  let hours = parseInt(m[1], 10);
  const minutes = m[2] ? parseInt(m[2], 10) : 0;
  const meridiem = m[3]?.toLowerCase();
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (meridiem === 'p' ? 12 : 0);
  }
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

export function buildReservationWindow(selection: Pick<ServiceBookingSelection, 'date' | 'time' | 'durationMinutes'>): ReservationWindow {
  const slot = parseSlotTime(selection.time);
  if (!slot) throw new Error(`Unrecognized slot time "${selection.time}"`);
  const pad = (n: number) => String(n).padStart(2, '0');
  const reservation_start = `${selection.date}T${pad(slot.hours)}:${pad(slot.minutes)}:00`;

  if (!selection.durationMinutes || selection.durationMinutes <= 0) {
    return { reservation_start, reservation_end: reservation_start };
  }

  const [year, month, day] = selection.date.split('-').map((part) => parseInt(part, 10));
  // A plain UTC-based Date used purely as a calendar/clock calculator (year/month/day/hour/minute
  // arithmetic) — never serialized with its own "Z"/offset, so the naive-local convention above
  // is preserved in the output string.
  const end = new Date(Date.UTC(year, (month || 1) - 1, day || 1, slot.hours, slot.minutes));
  end.setUTCMinutes(end.getUTCMinutes() + selection.durationMinutes);

  const endDate = `${end.getUTCFullYear()}-${pad(end.getUTCMonth() + 1)}-${pad(end.getUTCDate())}`;
  const endTime = `${pad(end.getUTCHours())}:${pad(end.getUTCMinutes())}:${pad(end.getUTCSeconds())}`;

  return { reservation_start, reservation_end: `${endDate}T${endTime}` };
}

// ─── Booking options (quantities only — never a price, see serviceBooking.ts) ──────────────────

export interface OrchestrateBookingOption {
  id: string;
  total_guest: number;
  // SAM-624 — the chosen rate group id (SAM-588); omitted when the line is not priced by group.
  rate_group?: string;
}

/**
 * `{optionId, quantity}` (this repo's shape) -> `{id, total_guest}` (pricing.py's "Requested
 * option" shape — see that module's docstring). `total_amount`/`price` are deliberately never
 * set: pricing.py ignores them for anything except its own tamper check, and CLAUDE.md's "Never
 * send a price from the client" rule applies here exactly as it does to the product path.
 */
export function buildBookingOptions(selection: Pick<ServiceBookingSelection, 'quantities'>): OrchestrateBookingOption[] {
  return selection.quantities.map((q) => ({
    id: q.optionId,
    total_guest: q.quantity,
    ...(q.rateGroup ? { rate_group: q.rateGroup } : {}),
  }));
}

// ─── Add-ons (SAM-590) — {id, qty} pairs, priced server-side ────────────────────────────────────

export interface OrchestrateAddOn {
  id: string;
  qty: number;
}

/**
 * `{id, qty}` (this repo's AddOnSelection shape, already `qty > 0`-filtered by
 * buildServiceBookingSelection) -> the exact `booking_data.add_ons` shape orchestrate_booking_payment
 * expects (sam-lambda-functions PR #419). No price/total here either — same rule as
 * buildBookingOptions.
 */
export function buildAddOns(selection: Pick<ServiceBookingSelection, 'addOns'>): OrchestrateAddOn[] {
  return (selection.addOns || []).map((a) => ({ id: a.id, qty: a.qty }));
}

// ─── on_behalf_of (guest + companions the form already collected) ──────────────────────────────

export interface OnBehalfOfGuest {
  name: string;
  last_name: string;
  phone_number?: string;
  email?: string;
}

export interface OnBehalfOfPayload {
  /** SAM-198 «Existing client»: an app user picked by the operator. Wins over main_guest server-side. */
  user_id?: number | string;
  main_guest?: OnBehalfOfGuest;
  business_guests: OnBehalfOfGuest[];
  children_count?: number;
}

/**
 * Builds the `on_behalf_of` field from the SAME guest/companion data the product/invitation path
 * already collects (`invitation.guests`) — see `_resolve_on_behalf_of_precheck`'s docstring in
 * booking_orchestration.py for the exact shape this must match. A walk-in («New client») is a fresh
 * `main_guest`, same as the product path's `business_access` POST. SAM-198: when the operator
 * picked an existing app user («Existing client»), only `user_id` identifies the main guest — no
 * `main_guest` is sent, so the backend books for that account instead of creating another one;
 * companions and children are sent either way.
 */
export function buildOnBehalfOf(params: {
  mainGuest: { firstName?: string; lastName?: string; phoneNumber?: string; email?: string; userId?: number | string | null };
  companions?: Array<{ firstName?: string; lastName?: string }>;
  childrenCount?: number;
}): OnBehalfOfPayload {
  const business_guests = (params.companions || []).map((c) => ({
    name: c.firstName || 'Companion',
    last_name: c.lastName || '',
  }));
  const children_count = params.childrenCount && params.childrenCount > 0 ? params.childrenCount : undefined;
  const userId = params.mainGuest.userId;
  if (userId !== undefined && userId !== null && userId !== '') {
    return { user_id: userId, business_guests, children_count };
  }
  return {
    main_guest: {
      name: params.mainGuest.firstName || 'Guest',
      last_name: params.mainGuest.lastName || '',
      phone_number: params.mainGuest.phoneNumber || undefined,
      email: params.mainGuest.email || undefined,
    },
    business_guests,
    children_count,
  };
}

// ─── Full request payload ────────────────────────────────────────────────────

export interface OrchestrateCardInput {
  number: string;
  expiration_month: string;
  expiration_year: string;
  security_code: string;
}

export interface OrchestrateAuthData {
  cavv?: string;
  eci?: string;
  eci_raw?: string;
  pares_status?: string;
  xid?: string;
  indicator?: string;
  acs_transaction_id?: string;
  directory_server_transaction_id?: string;
  authentication_transaction_id?: string;
  strong_authentication?: Record<string, string>;
}

export interface OrchestratePayload {
  booking_data: {
    service_id: string;
    reservation_start: string;
    reservation_end: string;
    booking_options: OrchestrateBookingOption[];
    // SAM-590 — omitted entirely when there are no non-zero add-on quantities, so a booking with
    // no add-ons keeps sending exactly the pre-SAM-590 booking_data shape.
    add_ons?: OrchestrateAddOn[];
    // SAM-624 (SAM-591) — the cabana/unit being booked; omitted unless the service needs it.
    resource_key?: string;
  };
  on_behalf_of: OnBehalfOfPayload;
  idempotency_key: string;
  merchant_reference: string;
  // Present only when a charge is actually expected (the operator got past the credit-card step).
  // Free-to-book selections (every booking_option priced at 0) never reach the 3DS chain, so
  // there is no card/auth_data to send — the server itself decides the amount is 0 and skips the
  // gateway entirely (booking_orchestration.py's `if total_amount == 0` branch).
  card?: OrchestrateCardInput;
  auth_data?: OrchestrateAuthData;
  billing_address?: Record<string, unknown>;
}

/**
 * Assembles the exact body `orchestrate_booking_payment` expects. `card`/`auth_data` are omitted
 * entirely when `payment` is undefined — this is what lets a free booking (no card ever
 * collected) skip the 3DS chain and still submit.
 */
export function buildOrchestratePayload(params: {
  selection: ServiceBookingSelection;
  onBehalfOf: OnBehalfOfPayload;
  idempotencyKey: string;
  payment?: {
    card: OrchestrateCardInput;
    authData?: OrchestrateAuthData;
    billingAddress?: Record<string, unknown>;
  };
}): OrchestratePayload {
  const { reservation_start, reservation_end } = buildReservationWindow(params.selection);
  const add_ons = buildAddOns(params.selection);

  const payload: OrchestratePayload = {
    booking_data: {
      service_id: params.selection.serviceId,
      reservation_start,
      reservation_end,
      booking_options: buildBookingOptions(params.selection),
      ...(add_ons.length ? { add_ons } : {}),
      ...(params.selection.resourceKey ? { resource_key: params.selection.resourceKey } : {}),
    },
    on_behalf_of: params.onBehalfOf,
    idempotency_key: params.idempotencyKey,
    // Stable per attempt (derived from the same key, never regenerated on retry) — purely a
    // human-legible reference for CyberSource/Portal dashboards, not used for dedupe (that is
    // idempotency_key's job, both here and forwarded to the engine).
    merchant_reference: `SB_${params.idempotencyKey.replace(/-/g, '').slice(0, 16).toUpperCase()}`,
  };

  if (params.payment) {
    payload.card = params.payment.card;
    if (params.payment.authData) payload.auth_data = params.payment.authData;
    if (params.payment.billingAddress) payload.billing_address = params.payment.billingAddress;
  }

  return payload;
}

// ─── 3DS "card not authenticated" ─────────────────────────────────────────────

export const THREEDS_NOT_AUTHENTICATED_MESSAGE =
  'The card did not complete the 3DS verification. Try another card. / La tarjeta no completó la verificación 3DS. Prueba con otra tarjeta.';

// The explicit "not authenticated" state the enroll/validate/orchestrate responses carry. The field
// that holds it is read from any of the usual places, so one rename on the API side is a one-line change here.
const NOT_AUTHENTICATED = new Set([
  'not_authenticated', 'card_not_authenticated', 'unauthenticated', 'authentication_failed', 'authentication_rejected', 'threeds_not_authenticated',
]);
const NOT_AUTHENTICATED_FIELDS = ['error_code', 'code', 'status', 'authentication_status', 'auth_status'] as const;

export function isThreeDsNotAuthenticated(payload: any): boolean {
  if (!payload || typeof payload !== 'object') return false;
  // enroll and validate answer `data.authenticated` (bool); the orchestrate 400 answers `code: "CARD_NOT_AUTHENTICATED"`.
  if (payload.authenticated === false || payload.data?.authenticated === false) return true;
  return NOT_AUTHENTICATED_FIELDS.some((field) => {
    const value = payload[field];
    return typeof value === 'string' && NOT_AUTHENTICATED.has(value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_'));
  });
}

// ─── Error mapping ────────────────────────────────────────────────────────────

export interface OrchestrationErrorInfo {
  code: 'pricing' | 'not_authenticated' | 'missing_3ds' | 'capability_denied' | 'slot_taken' | 'payment_declined' | 'needs_recovery' | 'server' | 'network';
  message: string;
  // SAM-624 — the server's `error_code` when the rejection is a pricing one (pricing.py).
  pricingCode?: string;
}

/**
 * Turns an axios error (or the {statusCode, message} shape a couple of code paths still hand
 * back directly) from POST /bookings/payment/orchestrate into a message an operator can act on.
 * Per the brief: a 400 (missing 3DS auth_data), a 403 (missing book_on_behalf capability) and a
 * 409 (slot taken since the operator picked it) must each read differently — this is the single
 * place that decides how.
 */
export function mapOrchestrationError(error: any): OrchestrationErrorInfo {
  const status = error?.response?.status ?? error?.statusCode;
  const serverMessage: string | undefined = error?.response?.data?.message ?? error?.message;

  if (!status) {
    return {
      code: 'network',
      message: 'Could not reach the booking server. Check your connection and try again — it is safe to retry.',
    };
  }

  // SAM-624 — a PricingError arrives as 400 `{message, error_code}`. When the code is one this
  // admin knows, show the EN/ES copy instead of the server's English text with internal ids.
  const errorCode: unknown = error?.response?.data?.error_code;
  const pricingMessage = formatPricingError(errorCode, error?.response?.data?.details);
  if (pricingMessage) {
    return { code: 'pricing', message: pricingMessage, pricingCode: String(errorCode) };
  }

  if (isThreeDsNotAuthenticated(error?.response?.data)) {
    return { code: 'not_authenticated', message: THREEDS_NOT_AUTHENTICATED_MESSAGE };
  }

  switch (status) {
    case 400:
      return {
        code: 'missing_3ds',
        message: serverMessage || 'This booking needs 3D Secure authentication before it can be charged. Please complete the card verification step and try again.',
      };
    case 403:
      return {
        code: 'capability_denied',
        message: serverMessage || 'Your account is not authorized to create a booking on behalf of a guest for this business.',
      };
    case 409:
      return {
        code: 'slot_taken',
        message: serverMessage || 'This time slot was just taken by someone else. Please pick another date or time.',
      };
    case 402:
      return {
        code: 'payment_declined',
        message: serverMessage || 'The card was declined. Try a different card.',
      };
    case 202:
      return {
        code: 'needs_recovery',
        message: serverMessage || 'The payment went through but the booking could not be confirmed automatically. This has been recorded — the card was not charged again. Contact support before retrying.',
      };
    default:
      return {
        code: 'server',
        message: serverMessage || `The booking could not be completed (status ${status}). Please try again.`,
      };
  }
}
